/**
 * Course Service
 * Handles all course-related API operations with Supabase
 */

import { supabase, isSupabaseConfigured } from "./supabase";
import type {
  Course,
  CourseApiResponse,
  CreateCourseDto,
  UpdateCourseDto,
} from "@/types/course";

// Fallback data if Supabase is not configured
import instructorRos from "@/assets/instructor-ros-new.png";
import instructorBoth from "@/assets/instructor-both-new.png";
import instructorKlau from "@/assets/instructor-klau-new.png";
import leaderStylingImg from "@/assets/leader-styling.png";
import partnerworkImg from "@/assets/partnerwork.png";
import followerStylingImg from "@/assets/follower-styling.png";

// Map database image paths to Vite-imported assets
const IMAGE_PATH_MAP: Record<string, string> = {
  '/assets/follower-styling.png': followerStylingImg,
  '/assets/partnerwork.png': partnerworkImg,
  '/assets/leader-styling.png': leaderStylingImg,
  '/assets/instructor-klau-new.png': instructorKlau,
  '/assets/instructor-both-new.png': instructorBoth,
  '/assets/instructor-ros-new.png': instructorRos,
};

const resolveImagePath = (path: string): string =>
  IMAGE_PATH_MAP[path] || path;

const FALLBACK_COURSES: Course[] = [
  // Main 3 Courses - Top Row
  {
    id: '3',
    title: "Follower Styling",
    tagline: "Grace • Expression • Natural Flow",
    description:
      "Refine grace, rhythm, and self-expression. Elevate your dance with fluid styling and natural connection to your partner.",
    instructor: "Klau",
    instructorImage: instructorKlau,
    staticImage: followerStylingImg,
    gradient: "from-primary/15 to-secondary/15",
    videoUrl: "https://media.klauyros.artoverse.club/followers_styling.mp4",
    order: 1,
    isActive: true,
    stripePriceId: "price_1SL2Z6EICPuf8WBFsT97b7OM",
  },
  {
    id: '2',
    title: "Partnerwork",
    tagline: "Connection • Timing • Communication",
    description:
      "Build real connection through timing, flow, and communication. Learn techniques that turn coordination into effortless harmony.",
    instructor: "Klau y Ros",
    instructorImage: instructorBoth,
    staticImage: partnerworkImg,
    gradient: "from-secondary/20 to-secondary/5",
    videoUrl: "https://media.klauyros.artoverse.club/partnerwork.mp4",
    order: 2,
    isActive: true,
    stripePriceId: "price_1SKTzIEICPuf8WBFK2vx67Rc",
  },
  {
    id: '1',
    title: "Leader Styling",
    tagline: "Lead Technique • Confidence • Flow",
    description:
      "Master confident movement and musical precision. Develop your lead technique, body control, and style that commands presence.",
    instructor: "Ros",
    instructorImage: instructorRos,
    staticImage: leaderStylingImg,
    gradient: "from-primary/20 to-primary/5",
    videoUrl: "https://media.klauyros.artoverse.club/ros_styling.mp4",
    order: 3,
    isActive: true,
    stripePriceId: "price_1SKU0KEICPuf8WBFcTAQQhub",
  },
  // New Courses - Bottom Row
  {
    id: '4',
    title: "TEAMS – VUELA",
    tagline: "Performance • Teamwork • Excellence",
    description:
      "Join the next generation of Vuela teams led by Klau y Ros. Develop precision, stage presence, and teamwork through guided rehearsals that elevate performance to international standards.",
    instructor: "Klau y Ros",
    instructorImage: instructorBoth,
    staticImage: "/assets/teamvuelanew.jpg",
    gradient: "from-orange-500/20 to-yellow-500/10",
    order: 4,
    isActive: true,
    badge: "Invite Only",
    ctaText: "Apply Now",
  },
  {
    id: '5',
    title: "CHOREOGRAPHY – NIVELES",
    tagline: "Precision • Artistry • Performance",
    description:
      "Master advanced partnerwork technique, musical interpretation, and stage confidence through structured choreography sessions.",
    instructor: "Klau y Ros",
    instructorImage: instructorBoth,
    staticImage: "/assets/niveleschoreonew.jpg",
    gradient: "from-purple-500/20 to-pink-500/10",
    order: 5,
    isActive: true,
    ctaText: "Start Learning",
    stripePriceId: "price_1SSlLGEICPuf8WBFueUISl3J",
  },
  {
    id: '6',
    title: "Klau's Ladies Team",
    tagline: "Empowerment • Sisterhood • Excellence",
    description:
      "Train with Klau to refine styling, confidence, and stage presence. Represent your city with professionalism and excellence on the global stage.",
    instructor: "Klau",
    instructorImage: instructorKlau,
    staticImage: "/assets/ladiesteamnew.jpg",
    gradient: "from-rose-500/20 to-pink-500/10",
    order: 6,
    isActive: true,
    badge: "Invite Only",
    ctaText: "Apply Now",
    stripePriceId: "price_1STK4XEICPuf8WBFIbx3F70L",
  },
];

type DbCourse = {
  id: string;
  title: string;
  tagline: string;
  description: string;
  instructor: string;
  instructor_image: string;
  static_image: string;
  video_url: string | null;
  gradient: string;
  order: number;
  is_active: boolean;
  badge?: string | null;
  cta_text?: string | null;
  stripe_price_id?: string | null;
  created_at?: string;
  updated_at?: string;
};

const mapDbCourse = (course: DbCourse): Course => ({
  id: course.id,
  title: course.title,
  tagline: course.tagline,
  description: course.description,
  instructor: course.instructor,
  instructorImage: resolveImagePath(course.instructor_image),
  staticImage: resolveImagePath(course.static_image),
  videoUrl: course.video_url ?? undefined,
  gradient: course.gradient,
  order: course.order,
  isActive: course.is_active,
  badge: course.badge ?? undefined,
  ctaText: course.cta_text ?? undefined,
  stripePriceId: course.stripe_price_id ?? undefined,
  createdAt: course.created_at,
  updatedAt: course.updated_at,
});

const toDbPayload = (course: Partial<Course>) => {
  const payload: Record<string, unknown> = {
    title: course.title,
    tagline: course.tagline,
    description: course.description,
    instructor: course.instructor,
    instructor_image: course.instructorImage,
    static_image: course.staticImage,
    video_url: course.videoUrl,
    gradient: course.gradient,
    order: course.order,
    is_active: course.isActive,
    badge: course.badge,
    cta_text: course.ctaText,
  };

  Object.keys(payload).forEach((key) => {
    if (payload[key] === undefined) {
      delete payload[key];
    }
  });

  return payload;
};

/**
 * Fetch all active courses with timeout and fallback
 */
export const getCourses = async (): Promise<CourseApiResponse> => {
  // Return fallback if Supabase not configured
  if (!isSupabaseConfigured()) {
    return { data: FALLBACK_COURSES, error: null };
  }

  try {
    // Add timeout to prevent hanging requests
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error("Request timeout")), 10000); // 10 second timeout
    });

    const fetchPromise = supabase
      .from("courses")
      .select(
        "id, title, tagline, description, instructor, instructor_image, static_image, video_url, gradient, order, is_active, badge, cta_text, stripe_price_id, created_at, updated_at"
      )
      .eq("is_active", true)
      .order("order", { ascending: true });

    const { data, error } = (await Promise.race([
      fetchPromise,
      timeoutPromise,
    ])) as { data: DbCourse[] | null; error: Error | null };

    if (error) {
      return {
        data: FALLBACK_COURSES,
        error: error as Error,
      };
    }

    // If we got data from Supabase, use it
    if (data && data.length > 0) {
      return {
        data: data.map(mapDbCourse),
        error: null,
      };
    }

    // If no data from Supabase, use fallback
    return {
      data: FALLBACK_COURSES,
      error: null,
    };
  } catch (error) {
    // Always return fallback on any error (network, timeout, etc.)
    return {
      data: FALLBACK_COURSES,
      error: error as Error,
    };
  }
};

/**
 * Fetch a single course by ID with fallback
 */
export const getCourseById = async (id: string): Promise<Course | null> => {
  // Check fallback first
  const fallbackCourse = FALLBACK_COURSES.find((c) => c.id === id);
  
  if (!isSupabaseConfigured()) {
    return fallbackCourse || null;
  }

  try {
    // Add timeout to prevent hanging requests
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error("Request timeout")), 10000);
    });

    const fetchPromise = supabase
      .from("courses")
      .select(
        "id, title, tagline, description, instructor, instructor_image, static_image, video_url, gradient, order, is_active, badge, cta_text, stripe_price_id, created_at, updated_at"
      )
      .eq("id", id)
      .single();

    const { data, error } = (await Promise.race([
      fetchPromise,
      timeoutPromise,
    ])) as { data: DbCourse | null; error: Error | null };

    if (error) {
      return fallbackCourse || null;
    }
    
    return data ? mapDbCourse(data) : fallbackCourse || null;
  } catch (error) {
    return fallbackCourse || null;
  }
};

/**
 * Create a new course
 */
export const createCourse = async (course: CreateCourseDto): Promise<Course | null> => {
  if (!isSupabaseConfigured()) {
    return null;
  }

  try {
    const payload = toDbPayload({ ...course, isActive: course.isActive ?? true });

    const { data, error } = await supabase
      .from("courses")
      .insert([payload])
      .select(
        "id, title, tagline, description, instructor, instructor_image, static_image, video_url, gradient, order, is_active, badge, cta_text, stripe_price_id, created_at, updated_at"
      )
      .single();

    if (error) throw error;
    return data ? mapDbCourse(data as DbCourse) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Update an existing course
 */
export const updateCourse = async (course: UpdateCourseDto): Promise<Course | null> => {
  if (!isSupabaseConfigured()) {
    return null;
  }

  try {
    const { id, ...updates } = course;
    const payload = toDbPayload(updates);

    const { data, error } = await supabase
      .from("courses")
      .update(payload)
      .eq("id", id)
      .select(
        "id, title, tagline, description, instructor, instructor_image, static_image, video_url, gradient, order, is_active, badge, cta_text, stripe_price_id, created_at, updated_at"
      )
      .single();

    if (error) throw error;
    return data ? mapDbCourse(data as DbCourse) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Delete a course (soft delete by setting isActive to false)
 */
export const deleteCourse = async (id: string): Promise<boolean> => {
  if (!isSupabaseConfigured()) {
    return false;
  }

  try {
    const { error } = await supabase
      .from("courses")
      .update({ is_active: false })
      .eq("id", id);

    if (error) throw error;
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Subscribe to real-time course updates
 */
export const subscribeToCourses = (callback: (courses: Course[]) => void) => {
  if (!isSupabaseConfigured()) {
    return null;
  }

  const subscription = supabase
    .channel("courses_channel")
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "courses",
      },
      async () => {
        // Refetch courses on any change
        const { data } = await getCourses();
        if (data) {
          callback(data);
        }
      }
    )
    .subscribe();

  return subscription;
};


